import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import '../styles.css';

const UpdateUser = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState({});
  const [currentUserId] = useState(localStorage.getItem('id'));
  const [userData, setUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    contact_number: '',
    role: '',
    active_role: '',
    employee_code: '',
    marital_status: '',
    work_location: '',
    designation: '',
    department: '',
    blood_group: '',
    date_of_birth: '',
    work_anniversary: '',
    nationality: '',
    religion: '',
    official_email_id: '',
    personal_email_id: '',
    mailing_address: '',
    permanent_address: '',
    manager: '',
    is_active: true,
    is_staff: false,
    profile_photo: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [roleError, setRoleError] = useState('');
  const [rolesData, setRolesData] = useState([]);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    axiosInstance.get(`/user/${userId}/`)
      .then(response => {
        setUserData(response.data); 
      })
      .catch(console.error);

    axiosInstance.get(`/user/${currentUserId}/`)
      .then(response => {
        const userData = response.data;
        setCurrentUser({ ...userData, role: userData.role?.name || 'guest' });
      })
      .catch(console.error);
  }, [userId, currentUserId]);

  useEffect(() => {
    axiosInstance.get(`/users/${currentUserId}/roles/`)
      .then(res => {
        setRolesData([res.data.active_role, res.data.assigned_role]);
      })
      .catch(console.error);
  }, []);

  const handleSwitchRole = async () => {
    try {
      await axiosInstance.post('/switch-role/', { role_id: rolesData[1].id });
      navigate('/user-dashboard');
    } catch (err) {
      setRoleError(err.response?.data?.detail || "Failed to switch role");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    setUserData({ ...userData, profile_photo: file });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    const formData = new FormData();
    Object.keys(userData).forEach((key) => {
      formData.append(key, userData[key]);
    });
    
    axiosInstance.patch(`/user/${userId}/`, formData)
      .then(response => {
        setSuccess('User updated successfully!');
        setTimeout(() => {
          navigate(`/user/${userId}`);
        }, 1500);
      })
      .catch(err => {
        console.error(err);
        setError('Failed to update user. Please try again.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleLogout = () => {
    axiosInstance.post('/logout/', { refresh: localStorage.getItem('refresh') })
      .then(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('employee_code');
        localStorage.removeItem('assignedRole');
        localStorage.removeItem('id'); 
        window.location.href = '/';
      })
      .catch(console.error);
  }; 

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
      <Sidebar 
        isSidebarVisible={isSidebarVisible} 
        currentUser={currentUser} 
        activeRole={currentUser?.role}
        error={roleError}
        handleSwitchRole={handleSwitchRole}
        handleLogout={handleLogout} 
      />
      
      <main className="dashboard-main-content">
        <div className="dashboard-header-section">
          <h1>Update User Information</h1>
          <p>Edit and update employee details</p>
        </div>

        <div className="create-user-card">
          {error && (
            <div className="form-error-message">
              <span>!</span> {error}
            </div>
          )}
          
          {success && (
            <div className="form-success-message">
              <span>&#10003;</span> {success}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <div className="section-header">
                <span className="section-icon"></span>
                <h3>Personal Information</h3>
              </div>
              
              <div className="form-grid">
                <div className="form-group photo-upload-group">
                  <label>Profile Photo</label>
                  <div className="photo-upload-container">
                    <input 
                      type="file" 
                      name="profile_photo" 
                      onChange={handleProfilePhotoChange}
                      accept="image/*"
                    />
                    <div className="dropzone">
                      <p>Click to select new photo</p>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="first_name">First Name *</label>
                  <input 
                    type="text" 
                    name="first_name" 
                    value={userData.first_name} 
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="middle_name">Middle Name</label>
                  <input 
                    type="text" 
                    name="middle_name" 
                    id="middle_name" 
                    value={userData.middle_name || ''} 
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="last_name">Last Name *</label>
                  <input 
                    type="text" 
                    name="last_name"
                    value={userData.last_name} 
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input 
                    type="email" 
                    name="email" 
                    value={userData.email} 
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact_number">Contact Number *</label>
                  <input 
                    type="text" 
                    name="contact_number" 
                    value={userData.contact_number} 
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="gender">Gender</label>
                  <select name="gender" value={userData.gender || ''} onChange={handleChange}>
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="date_of_birth">Date of Birth</label>
                  <input 
                    type="date" 
                    name="date_of_birth" 
                    value={userData.date_of_birth || ''} 
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="blood_group">Blood Group</label>
                  <select name="blood_group" value={userData.blood_group || ''} onChange={handleChange}>
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="marital_status">Marital Status</label>
                  <select name="marital_status" value={userData.marital_status || ''} onChange={handleChange}>
                    <option value="">Select Status</option>
                    <option value="unmarried">Unmarried</option>
                    <option value="married">Married</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="nationality">Nationality</label>
                  <input 
                    type="text" 
                    name="nationality" 
                    value={userData.nationality || ''} 
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="religion">Religion</label>
                  <input 
                    type="text" 
                    name="religion" 
                    value={userData.religion || ''} 
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <span className="section-icon"></span>
                <h3>Official Information</h3>
              </div>
              
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="employee_code">Employee Code</label>
                  <input 
                    type="text" 
                    name="employee_code" 
                    value={userData.employee_code || ''} 
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="official_email_id">Official Email</label>
                  <input 
                    type="email" 
                    name="official_email_id" 
                    value={userData.official_email_id || ''} 
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="personal_email_id">Personal Email</label>
                  <input 
                    type="email" 
                    name="personal_email_id" 
                    value={userData.personal_email_id || ''} 
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="duty_of">Duty Of</label>
                  <input 
                    type="text" 
                    name="duty_of" 
                    value={userData.duty_of || ''} 
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="esic_no">ESIC No.</label>
                  <input 
                    type="text" 
                    name="esic_no" 
                    value={userData.esic_no || ''} 
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="uan_no">UAN No.</label>
                  <input 
                    type="text" 
                    name="uan_no" 
                    value={userData.uan_no || ''} 
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="department">Department</label>
                  <input 
                    type="text" 
                    name="department" 
                    value={userData.department || ''} 
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="designation">Designation</label>
                  <input 
                    type="text" 
                    name="designation" 
                    value={userData.designation || ''} 
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="work_anniversary">Work Anniversary</label>
                  <input 
                    type="date" 
                    name="work_anniversary" 
                    value={userData.work_anniversary || ''} 
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="permanent_address">Permanent Address</label>
                  <input 
                    type="text" 
                    name="permanent_address" 
                    value={userData.permanent_address || ''} 
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="mailing_address">Mailing Address</label>
                  <input 
                    type="text" 
                    name="mailing_address" 
                    value={userData.mailing_address || ''} 
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input 
                    type="password" 
                    name="password" 
                    value={userData.password || ''} 
                    onChange={handleChange}
                    placeholder="Leave blank to keep current"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirm_password">Confirm Password</label>
                  <input 
                    type="password" 
                    name="confirm_password" 
                    value={userData.confirm_password || ''} 
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  <>
                    <span>+</span> Update User
                  </>
                )}
              </button>
              
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => navigate(`/user/${userId}`)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default UpdateUser;
